import React, { useContext, useEffect, useState } from "react";
import Thumbnail from "./Thumbnail";
import studyReportContext from "../../../../context/studyreport/StudyReportContext";
import DropDown from "../../../../components/SVGComponents/Tools/DropDown";
import ThumbnailContext from "../../../../context/studyviewer/ThumbnailContext";
import ViewportContext from "../../../../context/studyviewer/ViewportContext";
import studyviewerContext from "../../../../context/studyviewer/StudyviewerContext";
import themeContext from "../../../../context/theme/ThemeContext";
import { hpKey } from "../../../../helpers/hangingProtocol/hpIdentity";
import StudyPreloadButton from "./StudyPreloadButton";
import { isNonImageSeries } from "../../helpers/seriesModality";


const ChildStudyThumbnailList = ({
  studyData,
  startIndex,
  studyIndex,
  priors,
}) => {
  const { getConsistentDateFormat } = useContext(studyReportContext);
  const { showImpressions } = useContext(ViewportContext);
  const { appliedHp } = useContext(studyviewerContext);
  const expandAllPriors = Boolean(appliedHp?.expandAllPriors);
  const [dropDownClicked, setDropDownClicked] = useState(expandAllPriors);
  const {
    impressionHoveredStudyId,
    setImpressionHoveredStudyId,
    seriesHovered,
    setSeriesHovered,
  } = useContext(ThumbnailContext);
  const {  themeName } = useContext(themeContext);
  const handleDropDownloaClick = () => {
    setDropDownClicked(!dropDownClicked);
  };

  // The hang resolves after this list has mounted, so the protocol's
  // expandAllPriors has to be applied when it arrives, not just as initial
  // state. Keyed on the protocol too, so switching protocols re-applies it and a
  // manual click in between wins until then.
  const appliedHpKey = hpKey(appliedHp);
  useEffect(() => {
    setDropDownClicked(expandAllPriors);
  }, [expandAllPriors, appliedHpKey]);

  let timer;

  const onHovered = (e, studyId) => {
    e.stopPropagation();
    timer = setTimeout(() => {
      setImpressionHoveredStudyId(studyId);
      setSeriesHovered(true);
    }, 300);
  };

  const onHoveredOut = (e) => {
    e.stopPropagation();
    clearTimeout(timer);
    setImpressionHoveredStudyId(0);
    setSeriesHovered(false);
  };

  useEffect(() => {
    if (!seriesHovered) {
      const element = document.getElementById(
        "thumbnail_container_" + impressionHoveredStudyId
      );
      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      }
    }
  }, [impressionHoveredStudyId]);

  // Only NON-PR/KO series are viewable thumbnails. PR/KO stacks are filtered out
  // of the pane, so the collapsed/expanded split must be based on the viewable
  // series — NOT the raw array index. Otherwise, when a PR/KO series occupies
  // array index 0 (now that they're correctly typed & filtered), the collapsed
  // "first series" slot rendered null and the whole study looked closed until the
  // user manually expanded it. Keep each stack's ORIGINAL index so the parallel
  // originalStacks / heatmapStacks / outlineStacks lookups stay aligned.
  const viewableStacks = (studyData.stacks ?? [])
    .map((stack, index) => ({ stack, index }))
    .filter(({ stack }) => stack.modality !== "KO" && stack.modality !== "PR");
  // The tile shown while collapsed is the first IMAGE series when there is one
  // (not an SR / PDF / ECG … that happens to sort first); the rest keep order.
  const firstImagePos = viewableStacks.findIndex(({ stack }) => !isNonImageSeries(stack));
  const firstPos = firstImagePos > 0 ? firstImagePos : 0;
  const firstViewable = viewableStacks[firstPos];
  const restViewable  = viewableStacks.filter((_, pos) => pos !== firstPos);

  return (
    <React.Fragment key={studyIndex}>
      <div className="thumbnail-list-sticky-header">
        {/* <span> */}
        {viewableStacks.length > 1 &&
          (dropDownClicked ? (
            <div className="cursor-pointer" onClick={handleDropDownloaClick}>
              <DropDown color={themeName === "light" ? "black" : "white"} />
            </div>
          ) : (
            <div
              className="cursor-pointer"
              onClick={handleDropDownloaClick}
              style={{ transform: "rotate(270deg)" }}
            >
              <DropDown color={themeName === "light" ? "black" : "white"} />
            </div>
          ))}
        {/* </span> */}
        <span>
          {getConsistentDateFormat(studyData.studyDateDisplay).slice(0, 9)}
        </span>
        <span className="thumbnail-list-header-end">
          {getConsistentDateFormat(studyData.studyDateDisplay).slice(10)}
          <StudyPreloadButton
            entries={viewableStacks.map(({ stack, index }) => ({ index: startIndex + index, stack }))}
          />
        </span>
      </div>
      {firstViewable && (() => {
        const { stack, index } = firstViewable;
        return (
          <div
            id={`thumbnail_container_${stack.studyId}`}
            className={`${
              stack.studyId == impressionHoveredStudyId && showImpressions
                ? "highlight-thumbnail"
                : ""
            }`}
            key={startIndex + index}
            style={{ paddingTop: "20px" }}
            onMouseEnter={(e) => onHovered(e, stack.studyId)}
            onMouseLeave={(e) => onHoveredOut(e)}
          >
            <Thumbnail
              stack={stack}
              index={startIndex + index}
              originalStack={studyData.originalStacks[index]}
              heatmapStack={studyData.heatmapStacks[index]}
              outlineStack={studyData.outlineStacks[index]}
              isPriors={studyIndex === 0 ? false : true}
              studyIndex={studyIndex}
              priors={priors}
              loadThumbnail={true}
            />
          </div>
        );
      })()}
      {dropDownClicked &&
        restViewable.map(({ stack, index }) => (
          <div
            id={`thumbnail_container_${stack.studyId}`}
            className={`${
              stack.studyId == impressionHoveredStudyId && showImpressions
                ? "highlight-thumbnail"
                : ""
            }`}
            key={startIndex + index}
            style={{ paddingTop: "20px" }}
            autoFocus={stack.studyId == impressionHoveredStudyId}
            onMouseEnter={(e) => onHovered(e, stack.studyId)}
            onMouseLeave={(e) => onHoveredOut(e)}
          >
            <Thumbnail
              stack={stack}
              index={startIndex + index}
              originalStack={studyData.originalStacks[index]}
              heatmapStack={studyData.heatmapStacks[index]}
              outlineStack={studyData.outlineStacks[index]}
              isPriors={studyIndex === 0 ? false : true}
              studyIndex={studyIndex}
              priors={priors}
              startIndex={startIndex}
              lastIndex={startIndex + studyData.stacks.length - 1}
              dropDownClicked={dropDownClicked}
            />
            <br></br>
          </div>
        ))}
    </React.Fragment>
  );
};

export default ChildStudyThumbnailList;
